import { create } from 'zustand'
import { format } from 'date-fns'

interface AreaBounds {
  width: number
  height: number
}

interface FloorPlanStore {
  positions: Record<string, { x: number; y: number }>
  selectedTableId: string | null
  selectedAreaId: string | null
  snapToGrid: boolean
  gridSize: number
  zoom: number
  mode: 'layout' | 'live' | 'pos'
  dirtyIds: Set<string>
  areaBounds: Record<string, AreaBounds>
  timelineDate: string
  timelineHour: string | null   // null = All day; HH:MM when a specific time is selected
  activeLayoutId: string | null
  isEditingDateLayout: boolean  // true when editing a date-specific override (not the default layout)
  rejectedTableIds: Set<string>  // tables currently playing the rejection (red flash + shake) animation

  setPosition: (id: string, pos: { x: number; y: number }, areaId?: string) => void
  selectTable: (id: string | null) => void
  selectArea: (id: string | null) => void
  toggleSnap: () => void
  setZoom: (z: number) => void
  setMode: (m: 'layout' | 'live' | 'pos') => void
  markDirty: (id: string) => void
  clearDirty: () => void
  initPositions: (positions: Record<string, { x: number; y: number }>) => void
  setAreaBounds: (areaId: string, bounds: AreaBounds) => void
  setTimelineDate: (date: string) => void
  setTimelineHour: (hour: string | null) => void
  setActiveLayoutId: (id: string | null) => void
  setIsEditingDateLayout: (v: boolean) => void
  setRejectedTableIds: (ids: Set<string>) => void
}

function clampToArea(
  pos: { x: number; y: number },
  bounds: AreaBounds | undefined,
  tableWidth = 96,
  tableHeight = 64,
): { x: number; y: number } {
  if (!bounds) return pos
  return {
    x: Math.max(0, Math.min(pos.x, bounds.width - tableWidth)),
    y: Math.max(0, Math.min(pos.y, bounds.height - tableHeight)),
  }
}

export const useFloorPlanStore = create<FloorPlanStore>((set, get) => ({
  positions: {},
  selectedTableId: null,
  selectedAreaId: null,
  snapToGrid: true,
  gridSize: 16,
  zoom: (() => { try { return Math.max(0.25, Math.min(2, Number(localStorage.getItem('fp_zoom')) || 1)) } catch { return 1 } })(),
  mode: 'live',
  dirtyIds: new Set(),
  areaBounds: {},
  timelineDate: format(new Date(), 'yyyy-MM-dd'),
  timelineHour: format(new Date(), 'HH:mm'),
  activeLayoutId: null,
  isEditingDateLayout: false,
  rejectedTableIds: new Set<string>(),

  setPosition: (id, pos, areaId) =>
    set((state) => {
      const g = state.gridSize
      const snap = (v: number) => Math.round(v / g) * g
      let snapped = { x: snap(pos.x), y: snap(pos.y) }
      if (areaId) {
        const clamped = clampToArea(snapped, state.areaBounds[areaId])
        // Re-snap after clamp so the position stays on-grid even at area edges
        snapped = {
          x: Math.max(0, Math.floor(clamped.x / g) * g),
          y: Math.max(0, Math.floor(clamped.y / g) * g),
        }
      }
      return {
        positions: { ...state.positions, [id]: snapped },
        dirtyIds: new Set(state.dirtyIds).add(id),
      }
    }),

  selectTable: (id) => set({ selectedTableId: id, selectedAreaId: null }),
  selectArea: (id) => set({ selectedAreaId: id, selectedTableId: null }),
  toggleSnap: () => set((state) => ({ snapToGrid: !state.snapToGrid })),
  setZoom: (z) => {
    const clamped = Math.max(0.25, Math.min(2, z))
    try { localStorage.setItem('fp_zoom', String(clamped)) } catch {}
    set({ zoom: clamped })
  },
  setMode: (m) => set({ mode: m }),
  markDirty: (id) => set((state) => ({ dirtyIds: new Set(state.dirtyIds).add(id) })),
  clearDirty: () => set({ dirtyIds: new Set() }),
  initPositions: (positions) => set({ positions }),
  setAreaBounds: (areaId, bounds) =>
    set((state) => ({
      areaBounds: { ...state.areaBounds, [areaId]: bounds },
    })),
  setTimelineDate: (date) => set({ timelineDate: date }),
  setTimelineHour: (hour) => set({ timelineHour: hour }),
  setActiveLayoutId: (id) => set({ activeLayoutId: id }),
  setIsEditingDateLayout: (v) => set({ isEditingDateLayout: v }),
  setRejectedTableIds: (ids) => set({ rejectedTableIds: ids }),
}))
